import { PieChartCard } from '@/components/PieChartCard';
import { useTailwindTheme } from '@/hooks';
import { useGetRequestTotals } from '@/modules/Requests/queries';
import { useCallback, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { HoursKpiDetailsModal, RequestsKpiDetailsModal } from './components';
import { FilterContext } from '@/components/FilterProvider';

const RequestArchived = ({
  archivedRequests,
}: {
  archivedRequests: number;
}) => {
  const { t } = useTranslation();
  return (
    <p className="text-sm font-normal text-gray-400">{`${archivedRequests} ${t('chart_archived_requests')}`}</p>
  );
};

export const Charts = () => {
  const { colors } = useTailwindTheme();
  const { t } = useTranslation();

  const [openRequestsKpi, setOpenRequestsKpi] = useState(false);
  const [openHoursKpi, setOpenHoursKpi] = useState(false);

  const { showActiveOnly } = useContext(FilterContext);
  const { data: totals } = useGetRequestTotals(showActiveOnly);
  const unusedSeconds = totals?.unusedSeconds ?? 0;
  const hasHourData = (totals?.usedSeconds ?? 0) !== 0 || unusedSeconds !== 0;
  const isHourBalanceCritical = hasHourData && unusedSeconds <= -50 * 3600;
  const isHourBalanceWarning = hasHourData && unusedSeconds <= 50 * 3600;

  const onToggleExpandRequests = useCallback(
    () => setOpenRequestsKpi((prev) => !prev),
    [],
  );
  const onToggleExpandedHours = useCallback(
    () => setOpenHoursKpi((prev) => !prev),
    [],
  );

  return (
    <>
      <div className="flex flex-col gap-5 md:flex-row">
        <PieChartCard
          valueType={'number'}
          data={[
            {
              name: t('chart_open_requests'),
              value: totals?.requestsOpen ?? 0,
              fill: colors.blue[500],
            },
            {
              name: t('chart_finalized_requests'),
              value: totals?.requestsFinalized ?? 0,
              fill: colors.green.DEFAULT,
            },
            {
              name: t('chart_canceled_requests'),
              value: totals?.requestsCanceled ?? 0,
              fill: colors.orange.DEFAULT,
            },
          ]}
          chartTitle={t('chart_total_active_requests')}
          subtitle={t('chart_total')}
          onExpand={onToggleExpandRequests}
          extraInfo={
            !!totals?.requestsArchived && (
              <RequestArchived archivedRequests={totals.requestsArchived} />
            )
          }
        />
        <PieChartCard
          valueType={'number'}
          data={[
            {
              name: t('chart_Discovery_type'),
              value: totals?.nrRequestsOfDiscovery ?? 0,
              fill: colors.yellow.DEFAULT,
            },
            {
              name: t('chart_Development_type'),
              value: totals?.nrRequestsOfDevelopment ?? 0,
              fill: colors.turquoise.DEFAULT,
            },
            {
              name: t('chart_Diligence_type'),
              value: totals?.nrRequestsOfDilligence ?? 0,
              fill: colors.purple.DEFAULT,
            },
          ]}
          chartTitle={t('chart_request_typology')}
          subtitle={t('chart_total')}
          onExpand={onToggleExpandRequests}
        />
        <PieChartCard
          valueType={'hours'}
          data={[
            {
              name: t('chart_used_hours'),
              value: totals?.usedSeconds ?? 0,
              fill: colors.blue[500],
            },
            {
              name: t('chart_unused_hours'),
              value: unusedSeconds,
              fill: isHourBalanceCritical
                ? colors.red[400]
                : isHourBalanceWarning
                  ? colors.yellow.DEFAULT
                  : colors.gray[300],
              textColor: isHourBalanceCritical
                ? colors.red[800]
                : isHourBalanceWarning
                  ? colors.yellow[800]
                  : undefined,
            },
          ]}
          chartTitle={t('chart_contracted_hours')}
          subtitle={`${t('chart_total')}(h)`}
          onExpand={onToggleExpandedHours}
          extraInfo={totals?.extraHoursPackages.map((extraPackage, index) => (
            <p key={index} className="text-sm font-normal text-gray-400">
              {t('chart_extra_hour_package', extraPackage)}
            </p>
          ))}
        />
      </div>
      {openRequestsKpi && (
        <RequestsKpiDetailsModal
          isOpen={openRequestsKpi}
          onClose={onToggleExpandRequests}
        />
      )}
      {openHoursKpi && (
        <HoursKpiDetailsModal
          isOpen={openHoursKpi}
          onClose={onToggleExpandedHours}
        />
      )}
    </>
  );
};
